'use client';
import React, { useEffect, useState } from 'react';
import { auth } from '../../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getUserProjects, deleteProject } from '../../services/projectService';
import { convertGcsUrlsToSigned, getSignedUrl } from '../../services/storageService';
import { Link } from 'react-router-dom';
import { trackPageView, trackError } from '../../config/analytics';

function MyBrands() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [failedImages, setFailedImages] = useState(new Set());
  const [imageUrls, setImageUrls] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  useEffect(() => {
    // Track page view when component mounts
    trackPageView('my-brands');
    
    console.log('MyBrands: Starting useEffect');
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('MyBrands: Auth state changed, user:', user ? 'present' : 'null');
      if (user) {
        try {
          console.log('MyBrands: Attempting to fetch user projects');
          const userProjects = await getUserProjects(user.uid);
          console.log('MyBrands: Fetched projects:', userProjects?.length || 0);
          
          if (!userProjects || userProjects.length === 0) {
            console.log('MyBrands: No projects found');
            setProjects([]);
            setLoading(false);
            return;
          }

          // Process all projects
          console.log('MyBrands: Processing projects');
          const processedProjects = await Promise.all(
            userProjects.map(async (project) => {
              // Get the brand image URL
              const brandImageUrl = project.brands?.[0]?.imageUrl;
              console.log('MyBrands: Processing project:', {
                id: project.id,
                title: project.title,
                hasBrandImage: !!brandImageUrl
              });

              // Get fresh signed URL if we have a brand image
              if (brandImageUrl) {
                const signedUrl = await getFreshSignedUrl(project.id, brandImageUrl);
                if (signedUrl) {
                  project.brands[0].imageUrl = signedUrl;
                }
              }

              return project;
            })
          );

          console.log('MyBrands: Finished processing projects');
          setProjects(processedProjects);
        } catch (err) {
          console.error('MyBrands: Error fetching projects:', err);
          setError('Failed to load projects');
          trackError('fetch_projects_error', err.message);
        } finally {
          console.log('MyBrands: Setting loading to false');
          setLoading(false);
        }
      } else {
        console.log('MyBrands: No authenticated user found');
        setError('No authenticated user found');
        setLoading(false);
      }
    });

    return () => {
      console.log('MyBrands: Cleaning up auth listener');
      unsubscribe();
    };
  }, []);

  // Function to get a fresh signed URL for a brand image
  const getFreshSignedUrl = async (projectId, imageUrl) => {
    // console.log('Getting fresh signed URL for:', {
    //   projectId,
    //   imageUrl
    // });
    
    try {
      if (!imageUrl) {
        // console.log('No image URL provided');
        return null;
      }
      
      // Extract the path from the URL
      let path = '';
      if (imageUrl.includes('storage.googleapis.com/') || imageUrl.includes('storage.cloud.google.com/')) {
        // Remove query parameters
        const urlWithoutQuery = imageUrl.split('?')[0];
        // Get the path after the domain
        const pathMatch = urlWithoutQuery.match(/\/(caspian-\d+\/images\/[^/]+)/);
        if (pathMatch) {
          path = pathMatch[1];
          // console.log('Extracted path:', path);
        }
      }
      
      if (!path) {
        // console.log('Could not extract path from URL');
        return null;
      }
      
      // Build the GCS URL
      const gsUrl = `gs://${path}`;
      // console.log('Built GCS URL:', gsUrl);
      
      // Get the signed URL
      const signedUrl = await getSignedUrl(gsUrl);
      // console.log('Received signed URL:', signedUrl);
      
      return signedUrl;
    } catch (error) {
      // console.error('Error getting signed URL:', error);
      return null;
    }
  };

  const handleImageError = async (projectId, imageUrl) => {
    // console.log('Image load error, getting fresh signed URL');
    const signedUrl = await getFreshSignedUrl(projectId, imageUrl);
    if (signedUrl) {
      setImageUrls(prev => ({
        ...prev,
        [projectId]: signedUrl
      }));
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      await deleteProject(projectId);
      // console.log('Project deleted successfully');
      setProjects(projects.filter(p => p.id !== projectId));
      setShowDeleteConfirm(null);
    } catch (error) {
      // console.error('Error deleting project:', error);
      setError('Failed to delete project. Please try again.');
      trackError('delete_project_error', error.message);
    }
  };

  if (loading) {
    return (
      <div className="project-grid" style={{
        width: '100%',
        minHeight: '400px',
        border: '2px dashed rgba(255, 255, 255, 0.2)',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        padding: '20px'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #fff',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <span style={{ color: '#fff', fontSize: '16px' }}>Loading your projects...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="project-grid" style={{
        width: '100%',
        minHeight: '400px',
        border: '2px dashed rgba(255, 68, 68, 0.2)',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 68, 68, 0.05)',
        padding: '20px'
      }}>
        <div style={{ color: '#ff4444', fontSize: '16px', textAlign: 'center' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="project-grid" style={{
      width: '100%',
      minHeight: projects.length === 0 ? '200px' : 'auto',
      border: '2px dashed rgba(255, 255, 255, 0.2)',
      borderRadius: '8px',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      padding: '20px',
      marginTop: '-50px'
    }}>
      {projects.length === 0 ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: '20px',
          color: '#fff'
        }}>
          <span style={{ fontSize: '18px' }}>No Brands Found</span>
          <a
            href="/brands/new"
            className="butn butn-sm butn-bg main-colorbg radius-5"
            style={{ textDecoration: 'none' }}
          >
            <span className="text">Create Your First Brand</span>
          </a>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
          gap: '20px',
          width: '100%'
        }}>
          {projects.map((project) => {
            console.log('Project brands data:', {
              projectId: project.id,
              projectTitle: project.title,
              brands: project.brands,
              lastBrand: project.brands?.[project.brands.length - 1],
              lastBrandTagline: project.brands?.[project.brands.length - 1]?.tagLine
            });
            
            return (
              <div key={project.id} style={{ position: 'relative' }}>
                <Link
                  to={`/brands/${project.id}`}
                  style={{ textDecoration: 'none' }}
                >
                  <div
                    className="project-card"
                    style={{
                      position: 'relative',
                      aspectRatio: '1',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      ':hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 10px 20px rgba(0,0,0,0.2)'
                      }
                    }}
                  >
                    {project.brands && project.brands.length > 0 && project.brands[0].imageUrl ? (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.3)'
                      }}>
                        <img
                          src={imageUrls[project.id] || project.brands[0].imageUrl}
                          alt={`${project.title} - Brand Image`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            // console.log('Image failed to load:', {
                            //   projectId: project.id,
                            //   imageUrl: project.brands[0].imageUrl,
                            //   error: e
                            // });
                            handleImageError(project.id, project.brands[0].imageUrl);
                          }}
                          onLoad={() => {
                            // Get a fresh signed URL when the image loads successfully
                            getFreshSignedUrl(project.id, project.brands[0].imageUrl);
                          }}
                        />
                      </div>
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        color: '#fff',
                        fontSize: '14px'
                      }}>
                        No Preview
                      </div>
                    )}
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      padding: '15px',
                      background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                      color: '#fff'
                    }}>
                      <h6 style={{
                        margin: 0,
                        fontWeight: '500'
                      }}>
                        {project.brands?.length > 0 ? (project.brands[project.brands.length - 1].tagline?.length > 20 ? `${project.brands[project.brands.length - 1].tagline.slice(0, 20)}...` : project.brands[project.brands.length - 1].tagline) : project.title}
                      </h6>
                      <p style={{
                        margin: '5px 0 0',
                        fontSize: '12px',
                        opacity: 0.8
                      }}>
                        {project.createdAt ? new Date(project.createdAt.seconds * 1000).toLocaleDateString() : 'No date'}
                      </p>
                    </div>
                  </div>
                </Link>
                
                {/* Delete button */}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowDeleteConfirm(showDeleteConfirm === project.id ? null : project.id);
                  }}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    zIndex: 3
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
                  }}
                >
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  </svg>
                </button>

                {/* Delete confirmation tooltip */}
                {showDeleteConfirm === project.id && (
                  <div style={{
                    position: 'absolute',
                    top: '50px',
                    right: '10px',
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    color: '#fff',
                    padding: '12px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    whiteSpace: 'nowrap',
                    zIndex: 4,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    minWidth: '200px'
                  }}>
                    <div>Are you sure you want to delete this project?</div>
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      justifyContent: 'flex-end'
                    }}>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowDeleteConfirm(null);
                        }}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '4px',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          color: '#fff',
                          fontSize: '14px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteProject(project.id);
                        }}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '4px',
                          border: '1px solid rgba(253, 91, 56, 0.5)',
                          backgroundColor: 'rgba(253, 91, 56, 0.2)',
                          color: '#fd5b38',
                          fontSize: '14px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(253, 91, 56, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(253, 91, 56, 0.2)';
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MyBrands; 